import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { IAllProfessionalFisherCatchFilterOptions } from '@app/interfaces/professionalFisher';

export interface AllProfessionalFishersFilterState {
  selectedYear: number | null;
  selectedMonth: number | null;
  selectedFishtypeId: number | null;
  selectedNetNameId: string | null;
  selectedNetType: string | null;
  selectedUserIds: FilterIdsType;
  selectedUserIdsTemp: FilterIdsType;
  filterOptions: IAllProfessionalFisherCatchFilterOptions;
}

const initialState: AllProfessionalFishersFilterState = {
  selectedYear: null,
  selectedMonth: null,
  selectedFishtypeId: null,
  selectedNetNameId: null,
  selectedNetType: null,
  selectedUserIds: [],
  selectedUserIdsTemp: [],
  filterOptions: {
    years: [],
    months: [],
    fishtypes: [],
    netNames: [],
    netTypes: [],
    uniqueUsers: [],
  },
};

const allProfessionalFishersFiltersSlice = createSlice({
  name: 'allProfessionalFishersFilters',
  initialState,
  reducers: {
    setSelectedYear(state, action: PayloadAction<number | null>) {
      state.selectedYear = action.payload;
    },
    setSelectedMonth(state, action: PayloadAction<number | null>) {
      state.selectedMonth = action.payload;
    },
    setSelectedFishtypeId(state, action: PayloadAction<number | null>) {
      state.selectedFishtypeId = action.payload;
    },
    setSelectedNetNameId(state, action: PayloadAction<string | null>) {
      state.selectedNetNameId = action.payload;
    },
    setSelectedNetType(state, action: PayloadAction<string | null>) {
      state.selectedNetType = action.payload;
    },
    setSelectedUserIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedUserIds = action.payload;
    },
    setSelectedUserIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedUserIdsTemp = action.payload;
    },
    setFilterOptions(state, action: PayloadAction<IAllProfessionalFisherCatchFilterOptions>) {
      state.filterOptions = action.payload;
    },
    resetFilters(state) {
      state.selectedYear = initialState.selectedYear;
      state.selectedMonth = initialState.selectedMonth;
      state.selectedFishtypeId = initialState.selectedFishtypeId;
      state.selectedNetNameId = initialState.selectedNetNameId;
      state.selectedNetType = initialState.selectedNetType;
      state.selectedUserIds = initialState.selectedUserIds;
      state.selectedUserIdsTemp = initialState.selectedUserIdsTemp;
    },
  },
});

export const allProfessionalFishersFiltersActions = allProfessionalFishersFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: {
  allProfessionalFishersFilters: AllProfessionalFishersFilterState;
}) => {
  const { selectedYear, selectedMonth, selectedFishtypeId, selectedNetNameId, selectedNetType, selectedUserIds } =
    state.allProfessionalFishersFilters;

  return (
    selectedYear !== null ||
    selectedMonth !== null ||
    selectedFishtypeId !== null ||
    selectedNetNameId !== null ||
    selectedNetType !== null ||
    !!selectedUserIds.length
  );
};

export const activeFiltersCountSelector = (state: {
  allProfessionalFishersFilters: AllProfessionalFishersFilterState;
}) => {
  const { selectedYear, selectedMonth, selectedFishtypeId, selectedNetNameId, selectedNetType, selectedUserIds } =
    state.allProfessionalFishersFilters;

  let count = 0;
  if (selectedYear !== null) count++;
  if (selectedMonth !== null) count++;
  if (selectedFishtypeId !== null) count++;
  if (selectedNetNameId !== null) count++;
  if (selectedNetType !== null) count++;
  if (selectedUserIds.length) count++;

  return count;
};

// Memoized so the derived `fishtypeIds`/`netName`/`netType` arrays only get new references
// when the underlying selection actually changes — otherwise
// `useAppSelector(fetchParamsSelector, shallowEqual)` would see a "changed" value on every render
// (new array literals never `===` the previous ones) and re-trigger fetch effects in an infinite loop.
export const fetchParamsSelector = createSelector(
  (state: { allProfessionalFishersFilters: AllProfessionalFishersFilterState }) =>
    state.allProfessionalFishersFilters.selectedYear,
  (state: { allProfessionalFishersFilters: AllProfessionalFishersFilterState }) =>
    state.allProfessionalFishersFilters.selectedMonth,
  (state: { allProfessionalFishersFilters: AllProfessionalFishersFilterState }) =>
    state.allProfessionalFishersFilters.selectedFishtypeId,
  (state: { allProfessionalFishersFilters: AllProfessionalFishersFilterState }) =>
    state.allProfessionalFishersFilters.selectedNetNameId,
  (state: { allProfessionalFishersFilters: AllProfessionalFishersFilterState }) =>
    state.allProfessionalFishersFilters.selectedNetType,
  (state: { allProfessionalFishersFilters: AllProfessionalFishersFilterState }) =>
    state.allProfessionalFishersFilters.selectedUserIds,
  (selectedYear, selectedMonth, selectedFishtypeId, selectedNetNameId, selectedNetType, selectedUserIds) => ({
    year: selectedYear,
    month: selectedMonth,
    fishtypeIds: selectedFishtypeId ? [selectedFishtypeId] : [],
    netName: selectedNetNameId ? [selectedNetNameId] : [],
    netType: selectedNetType ? [selectedNetType] : [],
    userIds: selectedUserIds as number[],
  }),
);

export const allProfessionalFishersFilters = allProfessionalFishersFiltersSlice.reducer;
